import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAuthHeaders } from 'BackRoyalApi/queryShared';
import { useErrorLogService } from 'ErrorLogging';
import transformKeyCase from 'Utils/transformKeyCase';
import 'FrontRoyalUpload/locales/front_royal_upload/front_royal_upload-en.json';

// Matches the old `ngf-max-size="10MB"` on both upload inputs, using the codebase's MB convention
// (1024 * 1024, see maxSizeMBValidator).
const MAX_UPLOAD_SIZE_MB = 10;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

// The accept lists from the old ngf-select inputs.
export const TRANSCRIPT_ACCEPT =
    'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpg,image/png,image/jpeg,image/webp';
export const ENGLISH_DOCUMENT_ACCEPT =
    'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpg,image/webp,image/png,image/jpeg';

// Shown when the server rejects the file type, matching frontRoyalUpload's message.
const SUPPORTED_FORMATS_FOR_ERROR_MESSAGE = '.pdf, .doc, .docx, .jpg, .png';

type UploadResponse = { contents?: Record<string, Record<string, unknown>[]> };

// Uploads a single document via multipart form data, replacing frontRoyalUpload/ng-file-upload for
// the React Documents tab. Follows the same fetch + buildAuthHeaders approach as
// edit_account/useAvatarUpload.ts.
//
// Both endpoints use `params.expect(record: [...])`, so the file and its siblings must be nested
// under `record[...]`; only the English-document endpoint reads extra fields from the top level.
export function useUploadDocument() {
    const { t } = useTranslation('back_royal');
    const ErrorLogService = useErrorLogService();
    const [uploading, setUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const upload = useCallback(
        async <T>({
            url,
            file,
            recordFields = {},
            topLevelFields = {},
            responseKey,
        }: {
            url: string;
            file: File;
            recordFields?: Record<string, string>;
            topLevelFields?: Record<string, string>;
            responseKey: string;
        }): Promise<T | null> => {
            setErrorMessage(null);

            if (file.size > MAX_UPLOAD_SIZE_BYTES) {
                setErrorMessage(
                    t('front_royal_upload.front_royal_upload.max_file_size', { size: `${MAX_UPLOAD_SIZE_MB}MB` }),
                );
                return null;
            }

            setUploading(true);
            try {
                const formData = new FormData();
                formData.append('record[file]', file);
                Object.entries(recordFields).forEach(([key, value]) => formData.append(`record[${key}]`, value));
                Object.entries(topLevelFields).forEach(([key, value]) => formData.append(key, value));

                const response = await fetch(url, { method: 'POST', headers: buildAuthHeaders(), body: formData });

                // fetch only rejects on network errors, so a non-2xx would otherwise fall through
                // silently. 406 is how the server reports an unacceptable file (see frontRoyalUpload).
                if (!response.ok) {
                    if (response.status === 406) {
                        setErrorMessage(
                            t('front_royal_upload.front_royal_upload.unsupported_file_type', {
                                allowedTypes: SUPPORTED_FORMATS_FOR_ERROR_MESSAGE,
                            }),
                        );
                        return null;
                    }

                    setErrorMessage(t('front_royal_upload.front_royal_upload.unknown_file_issue'));
                    ErrorLogService.notify('Document upload failed', null, {
                        status: response.status,
                        url,
                        response_key: responseKey,
                    });
                    return null;
                }

                const data = (await response.json()) as UploadResponse;
                const record = data?.contents?.[responseKey]?.[0];
                if (!record) {
                    setErrorMessage(t('front_royal_upload.front_royal_upload.unknown_file_issue'));
                    ErrorLogService.notify('Document upload returned no record', null, {
                        url,
                        response_key: responseKey,
                    });
                    return null;
                }

                // The response is snake_cased; everything downstream reads the camelCased shape
                // convertCurrentUserFromIguana produces.
                return transformKeyCase<T>(record, { to: 'camelCase' });
            } catch (error) {
                setErrorMessage(t('front_royal_upload.front_royal_upload.unknown_file_issue'));
                ErrorLogService.notify('Document upload failed', error, { url, response_key: responseKey });
                return null;
            } finally {
                setUploading(false);
            }
        },
        [t, ErrorLogService],
    );

    return { upload, uploading, errorMessage };
}
